/**
 * PodcastFormDialog - Podcast create/edit form dialog
 *
 * Single Responsibility: Provides form interface for creating new podcasts
 * or editing existing podcast metadata (title, description, author, language,
 * explicit flag, podcast type, and Arabic translations).
 *
 * Does NOT handle: RSS imports, episode management, or tag assignment.
 */
package com.castly.admin.ui.podcasts.dialogs

import androidx.compose.runtime.*
import com.castly.admin.ui.*
import com.castly.admin.ui.theme.AppTheme
import com.castly.admin.ui.theme.BorderRadius
import com.castly.admin.ui.theme.Spacing
import com.castly.admin.ui.theme.Typography
import com.castly.admin.ui.theme.themeTransition
import com.castly.shared.dto.*
import org.jetbrains.compose.web.attributes.*
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

@Composable
fun PodcastFormDialog(
    podcast: PodcastDto?,
    onSave: (CreatePodcastRequest) -> Unit,
    onCancel: () -> Unit,
    defaultType: PodcastType? = null
) {
    val colors = AppTheme.colors
    // State for form fields
    val isEdit = podcast != null
    var title by remember { mutableStateOf(podcast?.title ?: "") }
    var description by remember { mutableStateOf(podcast?.description ?: "") }
    var author by remember { mutableStateOf(podcast?.author ?: "") }
    var language by remember { mutableStateOf(podcast?.language ?: "en") }
    var explicit by remember { mutableStateOf(podcast?.explicit ?: false) }
    var selectedType by remember { mutableStateOf(if (podcast != null) PodcastType.fromString(podcast.type) else defaultType ?: PodcastType.PODCAST) }

    // Type change confirmation states
    var showTypeChangeConfirm by remember { mutableStateOf(false) }
    var pendingNewType by remember { mutableStateOf<PodcastType?>(null) }

    // Translation states
    var arTitle by remember { mutableStateOf(podcast?.translations?.get("ar")?.title ?: "") }
    var arDescription by remember { mutableStateOf(podcast?.translations?.get("ar")?.description ?: "") }
    var arAuthor by remember { mutableStateOf(podcast?.translations?.get("ar")?.author ?: "") }

    fun handleSave() {
        val translations = mutableMapOf<String, PodcastTranslationData>()
        if (arTitle.isNotBlank()) {
            translations["ar"] = PodcastTranslationData(
                title = arTitle,
                description = arDescription.ifBlank { null },
                author = arAuthor.ifBlank { null }
            )
        }

        console.log("Saving podcast with type: ${selectedType.name}")
        val request = CreatePodcastRequest(
            title = title,
            description = description.ifBlank { null },
            author = author.ifBlank { null },
            language = language,
            explicit = explicit,
            translations = translations,
            type = selectedType.name
        )
        console.log("Request:", JSON.stringify(request))
        onSave(request)
    }

    Div({
        style {
            maxWidth(800.px)
            property("margin", "0 auto")
            themeTransition()
        }
    }) {
        // Header
        Div({
            style {
                display(DisplayStyle.Flex)
                justifyContent(JustifyContent.SpaceBetween)
                alignItems(AlignItems.Center)
                marginBottom(Spacing.xxl)
            }
        }) {
            H2({ style { margin(0.px); fontSize(Typography.xxl); fontWeight("600") } }) {
                Text(if (isEdit) "Edit Podcast" else "Create New Podcast")
            }
        }

        // Form card
        Div({
            style {
                backgroundColor(Color(colors.surface))
                borderRadius(BorderRadius.lg)
                border(1.px, LineStyle.Solid, Color(colors.border))
                padding(Spacing.xxl)
                themeTransition()
            }
        }) {
            // Main Fields
            FormField("Title *") {
                Input(InputType.Text) {
                    value(title)
                    onInput { title = it.value }
                    style { formInputStyle(colors) }
                }
            }

            FormField("Description") {
                TextArea {
                    value(description)
                    onInput { description = it.value }
                    style {
                        formInputStyle(colors)
                        height(100.px)
                        property("resize", "vertical")
                    }
                }
            }

            FormField("Author") {
                Input(InputType.Text) {
                    value(author)
                    onInput { author = it.value }
                    style { formInputStyle(colors) }
                }
            }

            FormField("Language") {
                Select({
                    onInput { language = it.value!! }
                    style { formInputStyle(colors) }
                }) {
                    Option("en", { if (language == "en") selected() }) { Text("English") }
                    Option("ar", { if (language == "ar") selected() }) { Text("Arabic") }
                    Option("es", { if (language == "es") selected() }) { Text("Spanish") }
                    Option("fr", { if (language == "fr") selected() }) { Text("French") }
                }
            }

            FormField("Podcast Type") {
                Select({
                    onInput { event ->
                        val newType = PodcastType.valueOf(event.value!!)
                        if (isEdit && newType != selectedType) {
                            pendingNewType = newType
                            showTypeChangeConfirm = true
                        } else {
                            selectedType = newType
                        }
                    }
                    style { formInputStyle(colors) }
                }) {
                    PodcastType.values().forEach { type ->
                        Option(type.name, { if (selectedType == type) selected() }) {
                            Text(type.displayName)
                        }
                    }
                }

                // Show current type
                if (isEdit && podcast != null) {
                    Div({
                        style {
                            fontSize(Typography.sm)
                            color(Color(colors.textMuted))
                            marginTop(Spacing.xs)
                        }
                    }) {
                        Text("Current: ${selectedType.displayName}${if (podcast.isLinked) " (RSS Linked)" else ""}")
                    }
                }
            }

            FormField("Explicit Content") {
                Div({
                    style {
                        display(DisplayStyle.Flex)
                        alignItems(AlignItems.Center)
                    }
                }) {
                    Input(InputType.Checkbox) {
                        checked(explicit)
                        onChange { explicit = it.value }
                        style { marginRight(Spacing.sm) }
                    }
                    Span { Text("This podcast contains explicit content") }
                }
            }

            // Arabic Translation Section
            H3({
                style {
                    fontSize(Typography.xl)
                    fontWeight("600")
                    marginTop(Spacing.xxl)
                    marginBottom(Spacing.lg)
                }
            }) { Text("Arabic Translation") }

            FormField("Arabic Title") {
                Input(InputType.Text) {
                    value(arTitle)
                    onInput { arTitle = it.value }
                    style { formInputStyle(colors) }
                }
            }

            FormField("Arabic Description") {
                TextArea {
                    value(arDescription)
                    onInput { arDescription = it.value }
                    style {
                        formInputStyle(colors)
                        height(100.px)
                        property("resize", "vertical")
                    }
                }
            }

            FormField("Arabic Author") {
                Input(InputType.Text) {
                    value(arAuthor)
                    onInput { arAuthor = it.value }
                    style { formInputStyle(colors) }
                }
            }
        }

        // Action buttons
        Div({
            style {
                display(DisplayStyle.Flex)
                justifyContent(JustifyContent.End)
                gap(Spacing.md)
                marginTop(Spacing.xxl)
            }
        }) {
            Button({
                onClick { onCancel() }
                style { secondaryButtonStyle(colors) }
            }) { Text("Cancel") }

            Button({
                onClick { handleSave() }
                if (title.isBlank()) disabled()
                style { primaryButtonStyle(disabled = title.isBlank(), colors = colors) }
            }) { Text(if (isEdit) "Update" else "Create") }
        }
    }

    // Type change confirmation dialog
    if (showTypeChangeConfirm && pendingNewType != null) {
        val newType = pendingNewType!!
        val isBookType = newType == PodcastType.BOOKS
        Div({
            style {
                position(Position.Fixed)
                top(0.px); left(0.px); right(0.px); bottom(0.px)
                backgroundColor(Color("rgba(0, 0, 0, 0.5)"))
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                justifyContent(JustifyContent.Center)
                property("z-index", "1100")
            }
            onClick {
                showTypeChangeConfirm = false
                pendingNewType = null
            }
        }) {
            Div({
                style {
                    backgroundColor(Color(colors.surface))
                    borderRadius(BorderRadius.lg)
                    padding(Spacing.xxl)
                    maxWidth(480.px)
                    width(90.percent)
                }
                onClick { it.stopPropagation() }
            }) {
                H3({
                    style {
                        margin(0.px); marginBottom(Spacing.lg)
                        fontSize(Typography.xl); fontWeight("600")
                        color(Color(colors.text))
                    }
                }) {
                    Text("Change Podcast Type?")
                }

                Div({
                    style {
                        fontSize(Typography.base)
                        color(Color(colors.textMuted))
                        marginBottom(Spacing.xl)
                        property("line-height", "1.6")
                    }
                }) {
                    Text("Change type from ")
                    Span({ style { fontWeight("600"); color(Color(colors.text)) } }) { Text(selectedType.displayName) }
                    Text(" to ")
                    Span({ style { fontWeight("600"); color(Color(colors.text)) } }) { Text(newType.displayName) }
                    Text("?")

                    if (isBookType) {
                        Div({
                            style {
                                marginTop(Spacing.md)
                                padding(Spacing.md)
                                backgroundColor(Color(if (colors == com.castly.admin.ui.theme.LightColors) "#fef3c7" else "#422006"))
                                borderRadius(BorderRadius.md)
                                fontSize(Typography.sm)
                            }
                        }) {
                            Text("Books can also be converted to an audiobook. Converting will create a new audiobook entry with the same metadata and episodes.")
                        }
                    }
                }

                Div({
                    style {
                        display(DisplayStyle.Flex)
                        gap(Spacing.md)
                        justifyContent(JustifyContent.FlexEnd)
                        flexWrap(FlexWrap.Wrap)
                    }
                }) {
                    Button({
                        onClick {
                            showTypeChangeConfirm = false
                            pendingNewType = null
                        }
                        style { secondaryButtonStyle(colors) }
                    }) { Text("Cancel") }

                    Button({
                        onClick {
                            selectedType = newType
                            showTypeChangeConfirm = false
                            pendingNewType = null
                        }
                        style { primaryButtonStyle(disabled = false, colors = colors) }
                    }) {
                        Text(if (isBookType) "Keep as Book-type Podcast" else "Confirm Change")
                    }
                }
            }
        }
    }
}
